import React, {
    forwardRef,
    memo,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
    ChangeEvent,
    FocusEvent,
    MouseEvent,
} from 'react';
import Label from "./Label";

interface InputProps {
    onChange?: (value: string) => void;
    onFocus?: (focused: boolean) => void;
    type?: string;
    placeholder?: string;
    icon?: React.ReactNode;
    iconPosition?: 'left' | 'right';
    size?: 'lg' | 'md';
    labels?: any;
    onLabelChange?: (tagId: string) => void;
    defaultValue?: string;
    id?: string;
    autoFocus?: boolean;
    selectAll?: boolean;
    className?: string;
    handleClear?: () => void;
    iconButton?: {
        icon: React.ReactNode;
        onClick: () => void;
        disabled?: boolean;
    };
}

interface InputRef {
    setFocus: (focus: boolean) => void;
    getText: () => string;
    setText: (text: string) => void;
}

const Input = memo(
    forwardRef<InputRef, InputProps>((props, ref) => {
        const {
            onChange,
            onFocus,
            placeholder,
            icon,
            iconPosition = 'right',
            type = 'text',
            size = "md",
            labels = [],
            onLabelChange,
            defaultValue = '',
            id,
            autoFocus = false,
            selectAll,
            className,
            handleClear,
            iconButton,
        } = props;

        const [value, setValue] = useState(defaultValue || '');
        const [isFocused, setIsFocused] = useState(false);

        const inputRef = useRef<HTMLInputElement>(null);

        const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
            setValue(event.target.value);
            if (onChange) onChange(event.target.value);
        }, [onChange]);

        useEffect(() => {
            if (autoFocus && inputRef.current) {
                inputRef.current.focus();
            }
            if (selectAll && inputRef.current && inputRef.current.value === value) {
                inputRef.current.select();
            }
        }, [autoFocus, selectAll, value]);

        useEffect(() => {
            if (selectAll && inputRef.current && inputRef.current.value === value && isFocused) {
                inputRef.current.select();
            }
        }, [isFocused, selectAll, value]);

        useImperativeHandle(ref, () => ({
            setFocus: (focus: boolean) => {
                if (focus && inputRef.current) {
                    inputRef.current.focus();
                } else if (inputRef.current) {
                    inputRef.current.blur();
                }
            },
            getText: () => {
                return inputRef.current ? inputRef.current.value : '';
            },
            setText: (text: string) => {
                setValue(text);
            },
        }));

        const handleClearValue = useCallback(() => {
            if (handleClear) handleClear();
            setValue('');
        }, [handleClear]);

        const handleIconButton = useCallback(() => {
            if (iconButton) {
                iconButton.onClick();
                if (handleClear) handleClear();
                setValue('');
            }
        }, [iconButton, handleClear]);

        const sizeMap = useMemo(() => ({

            "lg": { main: "min-h-[56px] px-[5px] gap-[5px] py-[5px]", child: "min-h-[46px] px-[12px]" },
            "md": { main: "min-h-[45px] px-[5px] gap-[5px] py-[5px]", child: "min-h-[35px] px-[12px]" },
        }), []);

        const Icon = useCallback(() => (
            <div className="color-black-500 flex justify-center items-center px-[12px]">
                {icon || ""}
            </div>
        ), [icon]);


        const handleDivClick = useCallback(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, []);

        const handleRemoveSelectedTags = useCallback((tagId: string) => {
            if (onLabelChange) onLabelChange(tagId);
        }, [onLabelChange]);

        return (
            <div
                className={`flex-grow  border border-gray-300 rounded-md flex-row flex gap-md items-center px-md pr-[6px] transition-all cursor-text hover:border-primary-500  ${
                    isFocused ? ' shadow-focus border-primary-500 ' : 'shadow-xs'} ${className}`}
                onClick={handleDivClick}
            >
                <div className={"inline-flex w-full items-center flex-wrap"}>
                    {icon && iconPosition === 'left' && <Icon />}
                    {labels && labels.length > 0 ? (
                        <div className={"inline-flex gap-[5px] flex-wrap"}>
                            <Label list={labels} onDelete={handleRemoveSelectedTags} />
                        </div>
                    ) : (
                        ""
                    )}
                    <input
                        type={type}
                        value={value}
                        onChange={handleChange}
                        placeholder={placeholder}
                        onFocus={() => {
                            setIsFocused(true);
                            if (onFocus) onFocus(true);
                        }}
                        onBlur={() => {
                            setIsFocused(false);
                            if (onFocus) onFocus(false);
                        }}
                        ref={inputRef}
                        id={id}
                        autoComplete="off"
                        className={`text-gray-900 flex flex-grow focus:outline-0 text-title-md placeholder-gray-300 placeholder:text-text-md font-normal`}
                    />
                    {value && (
                        <i
                            className="fa-solid fa-circle-xmark cursor-pointer text-[14px] color-black-300 px-[9px]"
                            onClick={handleClearValue}
                        ></i>
                    )}
                    {icon && iconPosition === 'right' && <Icon />}
                </div>
            </div>
        );
    })
);

export default Input;
export { Input };
